import { ProtectedRoute } from '@partiu/api/components/ProtectedRoute';
import { lazy, PropsWithChildren, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Navigate, Route } from 'react-router-dom';
import { bookstoreSlugResolver } from '../../../packages/ui/resolvers/slugResolvers';
import { RootTemplate } from '../../../packages/ui/templates/RootTemplate';
import './index.css';
import { ContactPage } from './pages/Contact';
import * as Sentry from '@sentry/react';
import { LegalPage } from './pages/Legal/Legal';
import { LoginPage } from './pages/Login/Login';
import { ProfilePage } from './pages/Profile/Profile';
import { MyBooksPage } from './pages/MyBooks/MyBooks';
import { browserTracingIntegration } from '@sentry/react';

const BookReader = lazy(() =>
  import('./pages/BookReader').then((m) => ({ default: m.BookReader }))
);
const AudioBookPage = lazy(() =>
  import('./pages/AudioBook').then((m) => ({ default: m.AudioBookPage }))
);
const PDFReaderPage = lazy(() =>
  import('./pages/PDFReader').then((m) => ({ default: m.PDFReaderPage }))
);
const ExchangeBookPage = lazy(() => import('./pages/ExchangeBook/ExchangeBookPage'));

// eslint-disable-next-line react-refresh/only-export-components
function BookstoreProtectedRoute({ children }: PropsWithChildren) {
  return (
    <ProtectedRoute loginPage="/login" showToast={false}>
      <Suspense fallback={null}>{children}</Suspense>
    </ProtectedRoute>
  );
}

const isProduction = process.env.REACT_APP_SENTRY_ENVIRONMENT === 'production';
if (isProduction) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT,
    tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE ?? '0.0') || 0.0,
    release: process.env.REACT_APP_SENTRY_RELEASE || 'unknown-release',
    integrations: [browserTracingIntegration],
  });
}

function dismissInitialLoader() {
  const el = document.getElementById('initial-loader');
  if (!el) return;
  el.classList.add('hide');
  setTimeout(() => el.remove(), 350);
  try {
    window.parent?.postMessage({ type: 'PARTIU_BOOKSTORE_READY' }, '*');
  } catch {
    // ignore cross-origin
  }
}

const isNativeIframe = new URLSearchParams(window.location.search).get('isNative') === 'true';

if (isNativeIframe) {
  (window as any).__dismissInitialLoader = dismissInitialLoader;
} else {
  requestAnimationFrame(() => requestAnimationFrame(dismissInitialLoader));
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <RootTemplate themeResolver={bookstoreSlugResolver}>
    <Route path={'/login'} element={<LoginPage />} />
    <Route
      path={'/'}
      element={<Navigate to="/biblioteca" />}
    />
    <Route
      path={'/perfil/*'}
      element={
        <BookstoreProtectedRoute>
          <ProfilePage />
        </BookstoreProtectedRoute>
      }
    />
    <Route
      path={'/acervo'}
      element={<Navigate to="/biblioteca" />}
    />
    <Route
      path={'/banca'}
      element={<Navigate to="/biblioteca" />}
    />
    <Route
      path={'/leitura/:id'}
      element={
        <BookstoreProtectedRoute>
          <BookReader />
        </BookstoreProtectedRoute>
      }
    />
    <Route
      path={'/audiobook/:id'}
      element={
        <BookstoreProtectedRoute>
          <AudioBookPage />
        </BookstoreProtectedRoute>
      }
    />
    <Route
      path={'/pdf/:id'}
      element={
        <BookstoreProtectedRoute>
          <PDFReaderPage />
        </BookstoreProtectedRoute>
      }
    />
    <Route
      path={'/biblioteca'}
      element={
        <BookstoreProtectedRoute>
          <MyBooksPage />
        </BookstoreProtectedRoute>
      }
    />
    <Route
      path={'/revistas'}
      element={
        <BookstoreProtectedRoute>
          <MyBooksPage />
        </BookstoreProtectedRoute>
      }
    />
    <Route
      path={'/recebidos/:bookUserId/troca'}
      element={
        <BookstoreProtectedRoute>
          <ExchangeBookPage />
        </BookstoreProtectedRoute>
      }
    />
    <Route
      path={'/recebidas'}
      element={
        <BookstoreProtectedRoute>
          <MyBooksPage />
        </BookstoreProtectedRoute>
      }
    />
    <Route path={'/legal/*'} element={<LegalPage />} />
    <Route path={'/contato'} element={<ContactPage />} />
    <Route path={'*'} element={<Navigate to="/" />} />
  </RootTemplate>
);
